import type { ComplianceAlertRow, ComplianceAlertsPage, ComplianceOverview, ComplianceUserRow, SurfaceStats } from '@core/api/complianceTypes';
import type { PillTone } from '@shared/ui';
import { formatRelative } from '@shared/formatters/date';
import { byDue, dueState, type DueState } from './dueState';
import { SURFACE_LABEL } from './labels';
import { ratePlain } from './plain';

/**
 * The overview's logic (display plan 2.2), pure so the tests pin it: the Controls table, the "Needs you now" queue
 * and the verdict line. The screen adds layout only.
 *
 * **The honesty rule** (health-first's, kept by the plan): a control reads "No problem found" only when the fields
 * that decide it are on the wire and prove it; silence is "Not confirmed", never a pass. A check that cannot fail on
 * this environment (no surface is set dark) reads "Not applicable here". Compliance review, 2026-09-27: the words
 * say what a check found ("No problem found"), never that a control "works".
 */

export type ControlState = 'ok' | 'attention' | 'action' | 'not-confirmed' | 'not-recorded' | 'off' | 'not-applicable' | 'not-available';

export const CONTROL_STATE_WORDS: Record<ControlState, { word: string; tone: PillTone }> = {
  ok: { word: 'No problem found', tone: 'gain' },
  attention: { word: 'Attention', tone: 'warning' },
  action: { word: 'Action today', tone: 'loss' },
  'not-confirmed': { word: 'Not confirmed', tone: 'neutral' },
  'not-recorded': { word: 'Not recorded', tone: 'neutral' },
  off: { word: 'Off here', tone: 'neutral' },
  'not-applicable': { word: 'Not applicable here', tone: 'neutral' },
  'not-available': { word: 'NOT AVAILABLE', tone: 'neutral' },
};

export interface ControlRow {
  key: 'checker' | 'disclaimer' | 'dark' | 'provenance' | 'copy' | 'evaluator' | 'channel';
  name: string;
  state: ControlState;
  detail: string;
}

const HOUR = 3_600_000;
/** The audit copy's own alarm (A6) fires after 30 hours without a finished run. */
const COPY_STALE_HOURS = 30;
/** The risk check runs hourly; two hours without a pass is a stall worth a look. */
const EVALUATOR_STALE_HOURS = 2;
/** The poller long-polls the queue continuously; ten minutes without a completed receive is a stall. */
const POLLER_STALE_MINUTES = 10;
const NOT_AVAILABLE = 'The AI records and their content-blind views are not in this database';

/**
 * The seven controls, in the plan's order. `s8Traders` is how many listed traders have S8 lit (AI used without
 * accepting the disclaimer in force), from the ranked list; null while it has not loaded.
 */
export function buildControls(
  o: ComplianceOverview,
  { s8Traders, usersComplete = true, now }: { s8Traders: number | null; usersComplete?: boolean; now: Date },
): ControlRow[] {
  const c = o.checks;
  const rows: ControlRow[] = [
    { key: 'checker', name: 'Checker', ...checker(o) },
    { key: 'disclaimer', name: 'Disclaimer', ...disclaimer(o, s8Traders, usersComplete) },
    { key: 'dark', name: 'AI where it should be off', ...dark(o) },
    {
      key: 'provenance', name: 'Records we cannot explain',
      ...(c.provenanceGaps > 0
        ? { state: 'action' as const, detail: `${c.provenanceGaps} ${c.provenanceGaps === 1 ? 'record' : 'records'} the firm cannot fully explain (audit check 5.6)` }
        : { state: 'ok' as const, detail: 'none (audit check 5.6)' }),
    },
    { key: 'copy', name: 'Audit copy', ...auditCopy(o, now) },
    { key: 'evaluator', name: 'Risk check (hourly)', ...evaluator(o, now) },
    { key: 'channel', name: 'AWS alert channel', ...channel(o, now) },
  ];
  if (!o.available) return rows.map(r => ({ ...r, state: 'not-available', detail: NOT_AVAILABLE }));
  return rows;
}

type StateAndDetail = Pick<ControlRow, 'state' | 'detail'>;

/**
 * The checker, per active checked surface (final review #4): the overview's `checks.checkerMode` is the window's
 * majority, which can hide one surface in Shadow, so every surface with rows (Consult chat has no checker by design)
 * is read on its own. One in Shadow is Attention, named; one with no mode recorded is Not recorded, named.
 */
function checker(o: ComplianceOverview): StateAndDetail {
  const ruleset = o.checks.rulesetVersion ?? 'not recorded';
  const active = o.surfaces.filter(s => s.last30d.rows > 0 && s.surface !== 'backtest-chat');
  if (active.length === 0) return { state: 'not-recorded', detail: 'no checked answer recorded in the window' };
  const names = (list: typeof active) => list.map(s => SURFACE_LABEL[s.surface] ?? s.surface).join(', ');
  const shadow = active.filter(s => s.checkerMode === 'shadow');
  if (shadow.length > 0) return { state: 'attention', detail: `Shadow on ${names(shadow)}: records, does not hold back; ruleset ${ruleset}` };
  const unrecorded = active.filter(s => s.checkerMode === null);
  if (unrecorded.length > 0) return { state: 'not-recorded', detail: `no checker mode recorded on ${names(unrecorded)}` };
  return { state: 'ok', detail: `Enforcing on every active checked surface (${active.length}); ruleset ${ruleset}, as recorded on the newest checked answer` };
}

function disclaimer(o: ComplianceOverview, s8Traders: number | null, usersComplete: boolean): StateAndDetail {
  const v = o.checks.disclaimerVersion;
  if (!v) return { state: 'not-recorded', detail: 'no disclaimer version in force is recorded' };
  if (s8Traders === null) return { state: 'not-confirmed', detail: `${v} in force; whether every AI use followed an acceptance is not loaded yet` };
  if (s8Traders > 0) return { state: 'action', detail: `${s8Traders} ${s8Traders === 1 ? 'trader' : 'traders'} used AI without accepting the version in force (S8)` };
  // The S8 count comes from the ranked list's first page; S8 scores no points, so its traders sort last (final review #5).
  if (!usersComplete) return { state: 'not-confirmed', detail: `${v} in force; the ranked list is longer than the page read here, so S8 is not confirmed for every trader` };
  const n = o.checks.acceptancesInWindow;
  return { state: 'ok', detail: `${v} in force, ${n} ${n === 1 ? 'acceptance' : 'acceptances'} in 30 days; no AI use without it found (S8)` };
}

function dark(o: ComplianceOverview): StateAndDetail {
  const surfaces = o.checks.darkSurfaces;
  if (surfaces.length === 0) return { state: 'not-applicable', detail: `no surface is set dark on ${o.environment}, so this check cannot fail here` };
  if (o.checks.darkSurfaceRows > 0) return { state: 'action', detail: `${o.checks.darkSurfaceRows} ${o.checks.darkSurfaceRows === 1 ? 'row' : 'rows'} on a surface that should be off` };
  return { state: 'ok', detail: `none on ${surfaces.map(s => SURFACE_LABEL[s] ?? s).join(', ')}` };
}

function auditCopy(o: ComplianceOverview, now: Date): StateAndDetail {
  if (!o.checks.auditExportEnabled) return { state: 'off', detail: 'the nightly audit copy is not scheduled on this environment' };
  const last = o.header.lastExportCompletedAt;
  if (!last) return { state: 'action', detail: 'scheduled, but no finished run is recorded' };
  const hours = (now.getTime() - Date.parse(last)) / HOUR;
  if (hours > COPY_STALE_HOURS) return { state: 'action', detail: `the last copy finished ${formatRelative(last)}, more than ${COPY_STALE_HOURS} hours ago` };
  return { state: 'ok', detail: `the last copy finished ${formatRelative(last)}` };
}

function evaluator(o: ComplianceOverview, now: Date): StateAndDetail {
  if (!o.checks.evaluatorEnabled) return { state: 'off', detail: 'the hourly risk check does not run in this backoffice' };
  const last = o.checks.lastEvaluationAt;
  if (!last) return { state: 'not-confirmed', detail: 'no pass reported since the service started' };
  const hours = (now.getTime() - Date.parse(last)) / HOUR;
  if (hours > EVALUATOR_STALE_HOURS) return { state: 'attention', detail: `the last pass was ${formatRelative(last)}; it should run every hour` };
  return { state: 'ok', detail: `the last pass was ${formatRelative(last)}` };
}

/**
 * The AWS channel from the poller's heartbeat (final review #6): a quiet week of no alerts is normal, so the last
 * message's age proves nothing; the last completed receive on the queue does. A stale receive is Stalled.
 */
function channel(o: ComplianceOverview, now: Date): StateAndDetail {
  const ch = o.checks.alertChannel;
  if (!ch.configured) return { state: 'off', detail: 'this backoffice does not hold the AWS inbox queue' };
  if (!ch.lastPollAt) return { state: 'not-confirmed', detail: 'set up here, but the poller has not reached the queue since the service started' };
  if ((now.getTime() - Date.parse(ch.lastPollAt)) / 60_000 > POLLER_STALE_MINUTES) {
    return { state: 'attention', detail: `Stalled: the poller last reached the queue ${formatRelative(ch.lastPollAt)}` };
  }
  if (!ch.lastMessageAt) return { state: 'not-confirmed', detail: 'the queue is polled, but no AWS message has arrived yet, so the path from AWS is not proven' };
  return { state: 'ok', detail: `the queue was polled ${formatRelative(ch.lastPollAt)}; the last AWS message arrived ${formatRelative(ch.lastMessageAt)}` };
}

// ─── The queue ──────────────────────────────────────────────────────────────────────────────

export type QueueItem =
  | { type: 'alert'; alert: ComplianceAlertRow; due: DueState }
  | { type: 'trader'; user: ComplianceUserRow; rank: number }
  | { type: 'control'; control: ControlRow };

export interface Queue {
  /** What the card lists: at most `QUEUE_CAP`. */
  items: QueueItem[];
  /** Every item, in order, before the cap (the verdict counts these). */
  all: QueueItem[];
  /** How many items the cap left out ("N more in the inbox"). */
  more: number;
  /** On dev: the items about firm or test accounts, folded under their own heading rather than in the queue. */
  firmOrTest: QueueItem[];
}

export const QUEUE_CAP = 10;

interface QueueInput {
  /** The open alerts (untouched or in hand). */
  openAlerts: readonly ComplianceAlertRow[];
  /** The ranked list, highest score first. */
  users: readonly ComplianceUserRow[];
  reviewScore: number;
  controls: readonly ControlRow[];
  environment: 'dev' | 'prod';
  now: Date;
}

/**
 * "Needs you now" (display plan 2.2, veto item 4): the open alerts (never a "marked reviewed" record), the traders
 * over the review line who have not been reviewed since their newest signal, and the controls that need attention
 * or action. Order: overdue first, then P1 (and a control needing action today), P2 (and a control needing
 * attention), P3, then the traders by rank; the oldest first within a band.
 *
 * Compliance review, 2026-09-27: on dev, alerts and traders about firm or test accounts (D7, from the ranked list's
 * `firmOrTestAccount`) are folded under their own heading instead of the queue: every disclaimer version bump turns
 * every tester into an S8 P1, and a queue full of testers teaches the reader to ignore P1s. On prod nothing folds.
 */
export function buildQueue({ openAlerts, users, reviewScore, controls, environment, now }: QueueInput): Queue {
  const firmIds = new Set(environment === 'dev' ? users.filter(u => u.firmOrTestAccount).map(u => u.userId) : []);
  const alerts = openAlerts
    .filter(a => a.status !== 'resolved' && a.kind !== 'user-reviewed')
    .sort(byDue(now))
    .map((a): QueueItem => ({ type: 'alert', alert: a, due: dueState(a, now) }));
  const traders = users
    .map((u, i) => ({ u, rank: i + 1 }))
    .filter(({ u }) => u.score >= reviewScore && (u.lastReviewedAt === null || (u.newestSignalAt !== null && u.newestSignalAt > u.lastReviewedAt)))
    .map(({ u, rank }): QueueItem => ({ type: 'trader', user: u, rank }));
  const unhealthy = controls
    .filter(c => c.state === 'action' || c.state === 'attention')
    .map((c): QueueItem => ({ type: 'control', control: c }));

  const isFirm = (i: QueueItem) =>
    (i.type === 'alert' && i.alert.subjectUserId !== null && firmIds.has(i.alert.subjectUserId))
    || (i.type === 'trader' && firmIds.has(i.user.userId));
  const band = (i: QueueItem): number => {
    if (i.type === 'alert') return i.due.kind === 'overdue' ? 0 : i.alert.severity === 'p1' ? 1 : i.alert.severity === 'p2' ? 2 : 3;
    if (i.type === 'control') return i.control.state === 'action' ? 1 : 2;
    return 4;
  };
  const merged = [...alerts, ...unhealthy, ...traders];
  // A stable sort by band keeps each list's own order (alerts by due, traders by rank) inside a band; controls sit
  // at the head of their band because they are appended before the traders and after the alerts of lower bands.
  const all = merged.filter(i => !isFirm(i)).map((i, idx) => ({ i, idx }))
    .sort((a, b) => band(a.i) - band(b.i) || controlFirst(a.i, b.i) || a.idx - b.idx)
    .map(x => x.i);
  return { items: all.slice(0, QUEUE_CAP), all, more: Math.max(0, all.length - QUEUE_CAP), firmOrTest: merged.filter(isFirm) };
}

/** Within a band, a control that needs action comes before the alerts of that band. */
function controlFirst(a: QueueItem, b: QueueItem): number {
  return (a.type === 'control' ? 0 : 1) - (b.type === 'control' ? 0 : 1);
}

// ─── The summary ────────────────────────────────────────────────────────────────────────────

/** The four numbers the overview opens with, and the one sentence above them. */
export interface OverviewSummary {
  /** Overdue alerts, open P1 alerts and system checks that need action today. */
  today: number;
  /** Of `today`, the alerts past their deadline. */
  overdue: number;
  /** Open P2 alerts not yet overdue (within 2 business days). */
  soon: number;
  /** Open P3 alerts not yet overdue (this week). */
  week: number;
  /** Traders over the review line who have not been reviewed since their newest signal. */
  traders: number;
  /** The system checks by what they found. */
  checks: {
    action: ControlRow[];
    attention: ControlRow[];
    /** Not confirmed yet: what a check could not prove, never read as a pass. */
    unconfirmed: ControlRow[];
    ok: number;
    total: number;
  };
  /** Items about firm or test accounts, folded out of the queue on dev. */
  folded: number;
  /** One short sentence, the worst state first: "29 things need action today, 22 of them overdue." */
  headline: string;
}

/**
 * The overview's summary (owner 2026-09-28: a summary first, the detail one click away), from the queue and the
 * controls. It counts only what the queue lists, and the checks tile says what could not be confirmed rather than
 * claiming a clean bill (the honesty rule).
 */
export function summaryOf(q: Queue, controls: readonly ControlRow[]): OverviewSummary {
  let today = 0;
  let overdue = 0;
  let soon = 0;
  let week = 0;
  let traders = 0;
  for (const i of q.all) {
    if (i.type === 'alert') {
      if (i.due.kind === 'overdue') { today++; overdue++; }
      else if (i.alert.severity === 'p1') today++;
      else if (i.alert.severity === 'p2') soon++;
      else week++;
    } else if (i.type === 'control' && i.control.state === 'action') today++;
    else if (i.type === 'trader') traders++;
  }
  const checks = {
    action: controls.filter(c => c.state === 'action'),
    attention: controls.filter(c => c.state === 'attention'),
    unconfirmed: controls.filter(c => c.state === 'not-confirmed'),
    ok: controls.filter(c => c.state === 'ok').length,
    total: controls.length,
  };
  const later = soon + week;
  const headline = today > 0
    ? `${today} ${today === 1 ? 'thing needs' : 'things need'} action today${overdue > 0 ? `, ${overdue} of them overdue` : ''}.`
    : later > 0
      ? `Nothing is due today. ${later} ${later === 1 ? 'alert is' : 'alerts are'} due later.`
      : traders > 0
        ? `Nothing is due. ${traders} ${traders === 1 ? 'trader waits' : 'traders wait'} for a review.`
        : checks.attention.length > 0
          ? `Nothing is due, but ${checks.attention.length === 1 ? 'a system check needs' : `${checks.attention.length} system checks need`} attention.`
          : 'All clear: nothing needs you now.';
  return { today, overdue, soon, week, traders, checks, folded: q.firmOrTest.length, headline };
}

// ─── The tab badge ──────────────────────────────────────────────────────────────────────────

/**
 * The Compliance tab's badge (display plan veto item 8): the open P1s (store-wide, from the strip's count) plus the
 * open alerts of lower severity that are overdue, so an overdue P1 is not counted twice.
 */
export function tabBadgeCount(page: Pick<ComplianceAlertsPage, 'openP1' | 'items'>, now: Date = new Date()): number {
  const overdueBelowP1 = page.items.filter(a => a.severity !== 'p1' && a.status !== 'resolved' && dueState(a, now).kind === 'overdue').length;
  return page.openP1 + overdueBelowP1;
}

// ─── The folded Numbers ─────────────────────────────────────────────────────────────────────

/** The final-block line of D6 (the plan's 5%). */
export const FINAL_RATE_LINE = 0.05;

/**
 * The one line on the folded Numbers (display plan 2.2): how many surfaces had activity, where the checker enforced,
 * and which final-block rate is over the 5% line, with the rate as a count out of the answers under 20.
 */
export function numbersSummary(surfaces: readonly SurfaceStats[], windowLabel = 'last 30 days'): string {
  const active = surfaces.filter(s => s.last30d.rows > 0);
  if (active.length === 0) return `No AI activity in ${windowLabel === 'last 30 days' ? '30 days' : windowLabel}.`;
  const enforcing = active.filter(s => s.checkerMode === 'enforce').length;
  const over = active.filter(s => s.last30d.finalBlockRate !== null && s.last30d.finalBlockRate > FINAL_RATE_LINE);
  const enforced = enforcing === active.length ? `all ${active.length}` : `${enforcing} of ${active.length}`;
  const rates = over.length === 0
    ? 'no rate over the 5% line'
    : `${over.length} ${over.length === 1 ? 'rate' : 'rates'} over the 5% line: ${over.map(s => `${SURFACE_LABEL[s.surface] ?? s.surface}, ${ratePlain(s.last30d.finalBlocks, s.last30d.firstAnswersEnforce)}`).join('; ')}`;
  return `${active.length} of ${surfaces.length} surfaces active, enforcing on ${enforced}, ${rates}`;
}
